import React, {CSSProperties, useCallback, useEffect, useId, useRef, useState} from 'react';
import {AttributeHeader} from './components/templates/table-components/header/AttributeHeader';
import {Header} from './components/templates/table-components/header/header';
import {IConfigDataAttributes, ITableDataEntry, ITableDataEntryValue} from './utils/interfaces';
import DataCell from './components/templates/table-components/datacell';
import {checkForNumberType, checkForUnwantedType, getHighlightColor, getValueColors} from './utils/utils';
import {ReportOptions} from './ProductTemplateTable';
import {Paginator} from 'primereact/paginator';

type ProductTemplateTableTransposedProps = {
    configData: any;
    templateData: Array<any>;

    selectedEntryIndex: number | undefined;
    selectedEntryId: string | undefined;
    selectedEntryBody: ITableDataEntry | undefined;
    page: number;
    setPage: (page: number) => void;

    reportOptions?: ReportOptions;
    itemsPerPage?: number
    foreignKeyWrap?: boolean;

    handleTableKeyDown(key: string): void;
    EditRowDataCell(val: any, index: number): JSX.Element;
    editedDataHandler(indexEntry: number, refName: string, newValue: any, newCell?: ITableDataEntryValue): void;
    onClickSign(entryIndex: number, referenceName: string): void;
};

const ProductTemplateTableTransposed = ({
                                            templateData,
                                            configData,
                                            handleTableKeyDown,
                                            selectedEntryBody,
                                            selectedEntryId,
                                            selectedEntryIndex,
                                            EditRowDataCell,
                                            editedDataHandler,
                                            onClickSign,
                                            reportOptions,
                                            itemsPerPage,
                                            page,
                                            setPage,
                                            foreignKeyWrap
                                        }: ProductTemplateTableTransposedProps) => {
    const divRef = useRef<HTMLDivElement>(null);
    const tableId = useId().replace(/[^a-zA-Z0-9]/g, '');

    const [columns, setColumns] = useState(itemsPerPage);

    const columnsPerPageOptions = itemsPerPage
        ? [itemsPerPage, 10, 20, 30]
            .sort((a, b) => a - b)
            .filter(function (x, i, a) {
                return a.indexOf(x) == i;
            })
        : [];

    const handleWheel = useCallback((event: any) => {
        if (event.altKey) {
            event.preventDefault();
            const div: HTMLDivElement = event.currentTarget as HTMLDivElement;
            div.scrollLeft += event.deltaY * 0.45;
        }
    }, []);

    useEffect(() => {
        if (reportOptions && document.getElementsByClassName('print-mode-enabled').length > 0) {
            const table = document.querySelector(`#${tableId}`);
            if (table) {
                const allChildren = table.querySelectorAll('*');

                if (reportOptions.fontSize) {
                    for (let i = 0; i < allChildren.length; i++) {
                        allChildren[i].style.fontSize = reportOptions?.fontSize;
                    }
                }
            }
        }
    }, []);

    useEffect(() => {
        if (divRef.current) {
            divRef.current.addEventListener('wheel', handleWheel);
        }

        return () => {
            if (divRef.current) {
                divRef.current.removeEventListener('wheel', handleWheel);
            }
        };
    }, [divRef, handleWheel]);

    const handlePageChange = (e) => {
        const newFirst = e.first;
        const newColumns = e.rows
        setPage(newFirst);
        setColumns(newColumns);
    };

    const Row = (configVal: IConfigDataAttributes, configIndex: number) => {
        if (templateData) {
            const slicedData = itemsPerPage ? templateData.slice(page, page + columns): templateData;
            return slicedData.map((val, i) => RowDataCell(val, i, configVal, configIndex));
        }
    };

    function RowDataCell(val: any, index: number, configVal: any, configIndex: number) {
        const dataValue = selectedEntryBody && selectedEntryIndex === index ? selectedEntryBody : val;
        const obj = dataValue.entries[configVal.referenceName];

        let color, bgColor;
        if (obj && obj.type && checkForNumberType(obj.type)) {
            [color, bgColor] = getValueColors(obj.value, {
                highlightRanges: configVal.bgHighlightRanges,
                defaultHighlightColor: configVal.highlightColor,
            });
        } else {
            [color, bgColor] = getHighlightColor(configVal.highlightColor);
        }
        return (
            <td key={val.id} style={styleHandler(color, bgColor, obj && obj.type)}
                className="right-border data-cell even-cell odd-cell custom-td my-td">
                <DataCell
                    key={val.id}
                    headerConfig={configVal}
                    dataValue={dataValue}
                    indexHeader={configIndex}
                    indexEntry={index}
                    changeHandler={editedDataHandler}
                    isInEditMode={selectedEntryId === val.id}
                    onClickSign={onClickSign}
                    foreignKeyWrap={foreignKeyWrap}
                />
            </td>
        );
    }

    const EditRowComponents = () => {
        if (templateData) {
            const slicedData = itemsPerPage ? templateData.slice(page, page + columns): templateData;
            return slicedData.map((val, i) => (
                <td key={val.id} style={{textAlign: 'center'}}
                    className="right-border data-cell even-cell odd-cell custom-td my-td">
                    {EditRowDataCell(val, i)}
                </td>
            ));
        }
    };

    const KeyColumn = (configVal: IConfigDataAttributes, configIndex: number) => {
        function KeyCell(val: any, index: number, configVal: any, configIndex: number) {
            const dataValue = selectedEntryBody && selectedEntryIndex === index ? selectedEntryBody : val;
            const obj = dataValue.entries[configVal.referenceName];

            let color, bgColor;
            if (obj && obj.type && checkForNumberType(obj.type)) {
                [color, bgColor] = getValueColors(obj.value, {
                    highlightRanges: configVal.bgHighlightRanges,
                    defaultHighlightColor: configVal.highlightColor,
                });
            } else {
                [color, bgColor] = getHighlightColor(configVal.highlightColor);
            }

            return (
                <th key={val.id} style={styleHandler(color, bgColor, obj && obj.type)}
                    className="right-border data-cell even-cell odd-cell custom-th my-th">
                    <DataCell
                        key={val.id}
                        headerConfig={configVal}
                        dataValue={dataValue}
                        indexHeader={configIndex}
                        indexEntry={index}
                        changeHandler={editedDataHandler}
                        isInEditMode={selectedEntryIndex === index}
                        onClickSign={onClickSign}
                    />
                </th>
            );
        }

        if (templateData) {
            const slicedData = itemsPerPage ? templateData.slice(page, page + columns): templateData;
            return slicedData.map((val, i) => KeyCell(val, i, configVal, configIndex));
        }
    };

    const styleHandler = (color: string | undefined, bgColor: string | undefined, type: string | undefined): CSSProperties | undefined => {
        if (checkForUnwantedType(type)) {
            if (color && bgColor) {
                return {
                    textAlign: 'center',
                    minWidth: '200px',
                    maxWidth: '200px',
                    color: color,
                    backgroundColor: bgColor,
                };
            }

            if (color) {
                return {
                    textAlign: 'center',
                    minWidth: '200px',
                    maxWidth: '200px',
                    color: color,
                };
            }

            if (bgColor) {
                return {
                    textAlign: 'center',
                    minWidth: '200px',
                    maxWidth: '200px',
                    backgroundColor: bgColor,
                };
            }
        }

        return {
            textAlign: 'center',
            minWidth: '200px',
            maxWidth: '200px',
        };
    };



    return (
        <div ref={divRef} onKeyDown={(e) => handleTableKeyDown(e.key)} className="transposed-table-wrapper">
            <table id={tableId} className="transposed-table">
                <thead className="transposed-thead">
                <tr>
                    {configData &&
                        configData.data.attributes.map((val: any, i: number) => {
                            if (val.referenceName === configData.keyColumn) {
                                return (
                                    <React.Fragment key={val.referenceName}>
                                        <AttributeHeader key={val.referenceName} attribute={val}/>
                                        {KeyColumn(val, i)}
                                    </React.Fragment>
                                );
                            }

                            return null;
                        })}
                </tr>
                </thead>
                <tbody className="transposed-tbody">
                {configData &&
                    configData.data.attributes.map((val: any, i: number) => {
                        if (val.referenceName !== configData.keyColumn) {
                            return (
                                <tr style={{margin: '5px'}} key={i.toString()}>
                                    <AttributeHeader attribute={val}/>
                                    {Row(val, i)}
                                </tr>
                            );
                        } else {
                            return null;
                        }
                    })}
                {configData && (
                    <tr className={'non-printable'}>
                        <Header/>
                        {EditRowComponents()}
                    </tr>
                )}
                </tbody>
            </table>
            {itemsPerPage && (
                <Paginator
                    totalRecords={templateData.length}
                    rows={columns}
                    first={page}
                    onPageChange={handlePageChange}
                    template={'CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown'}
                    currentPageReportTemplate={'Showing {first} to {last} of {totalRecords}'}
                    rowsPerPageOptions={columnsPerPageOptions}
                />
            )}
        </div>
    );
};
export default ProductTemplateTableTransposed;
